import { FC, useEffect, useMemo, useState } from 'react';
import { CourseData, FORMAT_LABELS, PricingOption, TYPE_LABELS } from '../types';
import { formatPaymentPeriod } from 'entities/course-catalog';
import { IconPython, IconJavaScript } from '../icons';
import styles from './CourseCard.module.scss';

type PricingStatus = 'loading' | 'ready' | 'error';

interface CourseCardProps {
  id: string;
  title: string;
  description: string;
  courses: CourseData[];
  format: 'online' | 'offline';
  programHref?: string;
  pricingStatus: PricingStatus;
  onSelectCourse: (course: CourseData, pricing: PricingOption) => void;
}

const PACE_LABELS: Record<string, string> = {
  'python-standard': 'Обычный темп',
  'python-intensive': 'Интенсивный темп',
  'javascript-standard': 'Обычный темп',
  'javascript-intensive': 'Интенсивный темп',
};

const getIcon = (iconName: string) => {
  const props = { width: 28, height: 28 };
  switch (iconName) {
    case 'python': return <IconPython {...props} />;
    case 'javascript': return <IconJavaScript {...props} />;
    default: return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d={iconName === 'p3' ? 'm7 7-5 5 5 5m10-10 5 5-5 5M14 4l-4 16' : 'M3 4h18v13H3zM8 21h8m-4-4v4'} /></svg>;
  }
};

const selectDefaultPricing = (course: CourseData, format: 'online' | 'offline') =>
  course.pricing.find(option => option.format === format && option.type === 'group') ||
  course.pricing.find(option => option.format === format) ||
  null;

export const CourseCard: FC<CourseCardProps> = ({
  id,
  title,
  description,
  courses,
  format,
  programHref,
  pricingStatus,
  onSelectCourse,
}) => {
  const [selectedCourseId, setSelectedCourseId] = useState(courses[0]?.id);

  useEffect(() => {
    if (!courses.some(course => course.id === selectedCourseId)) setSelectedCourseId(courses[0]?.id);
  }, [courses, selectedCourseId]);

  const course = useMemo(
    () => courses.find(item => item.id === selectedCourseId) || courses[0],
    [courses, selectedCourseId],
  );
  const pricing = course ? selectDefaultPricing(course, format) : null;
  const canEnroll = pricingStatus === 'ready' && Boolean(course && pricing);
  const hasPaceSelector = courses.length > 1;

  if (!course) return null;

  const priceText = pricingStatus === 'ready' && pricing
    ? `${pricing.price.toLocaleString('ru-RU')} ₽`
    : pricingStatus === 'error'
      ? 'Цена недоступна'
      : 'Загружаем цену…';
  const pricingContext = pricing
    ? `${pricing.type === 'group' ? 'В группе' : TYPE_LABELS[pricing.type]} · ${FORMAT_LABELS[format]}`
    : 'Актуальная цена';

  return (
    <article className={styles.courseCard} data-testid={`course-card-${id}`}>
      <div className={styles.heading}>
        <span className={styles.icon} aria-hidden="true">{getIcon(course.icon)}</span>
        <h3>{title}</h3>
      </div>

      <p className={styles.description}>{description}</p>

      {hasPaceSelector && (
        <div className={styles.paceGroup} role="group" aria-label={`Темп обучения: ${title}`}>
          {courses.map(item => (
            <button
              key={item.id}
              type="button"
              className={item.id === course.id ? styles.paceActive : styles.paceButton}
              aria-pressed={item.id === course.id}
              aria-label={PACE_LABELS[item.id] || item.trainingType || 'Выбрать темп'}
              data-testid={`course-pace-${item.id}`}
              onClick={() => setSelectedCourseId(item.id)}
            >
              {(PACE_LABELS[item.id] || item.trainingType || 'Выбрать темп').replace(' темп', '')}
            </button>
          ))}
        </div>
      )}

      <dl className={styles.details}>
        <div><dt>Длительность</dt><dd>{formatPaymentPeriod(course.durationMonths)}</dd></div>
        <div><dt>Занятия</dt><dd>{course.frequency}</dd></div>
      </dl>

      <div className={styles.footer}>
        <div className={styles.price} aria-live="polite" data-testid={`course-price-${id}`}>
          <span>{pricingContext}</span>
          <div className={styles.amount}>
            <strong className={pricingStatus !== 'ready' ? styles.priceStatus : undefined}>{priceText}</strong>
            {pricingStatus === 'ready' && pricing && <small>{course.billingUnit === 'course' ? 'за курс' : 'в месяц'}</small>}
          </div>
        </div>
        <div className={styles.actions}>
        {programHref && <a className={styles.programLink} href={programHref}>О программе <span aria-hidden="true">→</span></a>}
        <button
          type="button"
          className={styles.enrollButton}
          disabled={!canEnroll}
          data-testid={`course-enroll-${id}`}
          onClick={() => {
            if (course && pricing) onSelectCourse(course, pricing);
          }}
        >
          Записаться
        </button>
        </div>
      </div>
    </article>
  );
};
